import parsePhoneNumber from 'libphonenumber-js';
import {ChangeEvent, useEffect, useState} from 'react';
import {useAuth} from '../../contexts/AuthContext.tsx';
import {useUserAPI} from '../../hooks/useVerCasaAPI.tsx';
import userThree from '../../images/user/user-03.png';
import DefaultLayout from '../../layout/DefaultLayout.tsx';

// This is a simplified list. In a real application, you'd want a more comprehensive list.
const countryCodes = [
    {code: '+1', flag: '🇺🇸', name: 'USA'},
    // Add more country codes as needed
];

const User = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [photo, setPhoto] = useState<File | null>(null);

    const [photoPreview, setPhotoPreview] = useState<string | null>(null);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const {updateUser} = useUserAPI();
    const {user} = useAuth();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        countryCode: '+1',
        phoneNumber: '',
    });

    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
    });

    useEffect(() => {
        if (user) {
            let pn = parsePhoneNumber(user.phoneNumber);
            const countryCode = pn?.countryCallingCode;
            const localNumber = pn?.nationalNumber;

            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                countryCode: countryCode || '+1',
                phoneNumber: localNumber || '',
            });

            setPhotoPreview(user.photo || null);
        }
    }, [user]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const {name, value} = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
        // Clear error when user starts typing
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: '',
        }));
    };


    const handlePhotoChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setPhoto(file);

            // Create a preview of the selected photo
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    function ensurePlusPrefix(phoneNumber: string): string {
        // Remove any leading or trailing whitespace
        const trimmedNumber = phoneNumber.trim();

        // Check if the number already starts with '+'
        if (trimmedNumber.startsWith('+')) {
            return trimmedNumber;
        }

        // If it doesn't start with '+', add it
        return `+${trimmedNumber}`;
    }


    const validateForm = () => {
        let isValid = true;
        const newErrors = {...errors};

        if (formData.firstName.length < 3) {
            newErrors.firstName = 'First name must be at least 3 characters long';
            isValid = false;
        }

        if (formData.lastName.length < 3) {
            newErrors.lastName = 'Last name must be at least 3 characters long';
            isValid = false;
        }

        // E.164 format validation (simple version)
        let pn = parsePhoneNumber(ensurePlusPrefix(`${formData.countryCode}${formData.phoneNumber}`));
        if (!pn || !pn.isValid()) {
            newErrors.phoneNumber = 'Phone number must be in E.164 format (e.g., +11234567890)';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const pn = parsePhoneNumber(ensurePlusPrefix(`${formData.countryCode}${formData.phoneNumber}`));
            await updateUser({...formData, phoneNumber: pn?.number || undefined, photo: photo});
            setSuccess('Profile updated successfully!');
        } catch (err) {
            setError('Failed to update profile. Please try again.');
            console.error('Error updating profile:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <DefaultLayout>
            <div className="mx-auto max-w-270">

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-5 gap-8">
                        <div className="col-span-5 xl:col-span-3">
                            <div
                                className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                                <div className="border-b border-stroke py-4 px-7 dark:border-strokedark">
                                    <h3 className="font-medium text-black dark:text-white">
                                        Personal Information
                                    </h3>
                                </div>
                                <div className="p-7">


                                    <div className="mb-5.5 flex flex-col gap-5.5 sm:flex-row">
                                        <div className="w-full sm:w-1/2">
                                            <label
                                                className="mb-3 block text-sm font-medium text-black dark:text-white"
                                                htmlFor="firstName"
                                            >
                                                First Name
                                            </label>
                                            <input
                                                className="w-full rounded border border-stroke bg-gray py-3 px-4.5 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                                                type="text"
                                                name="firstName"
                                                id="firstName"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                placeholder="Enter your first name"
                                            />
                                            {errors.firstName && <p className="text-danger mt-1">{errors.firstName}</p>}
                                        </div>

                                        <div className="w-full sm:w-1/2">
                                            <label
                                                className="mb-3 block text-sm font-medium text-black dark:text-white"
                                                htmlFor="lastName"
                                            >
                                                Last Name
                                            </label>
                                            <input
                                                className="w-full rounded border border-stroke bg-gray py-3 px-4.5 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                                                type="text"
                                                name="lastName"
                                                id="lastName"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                placeholder="Enter your last name"
                                            />
                                            {errors.lastName && <p className="text-danger mt-1">{errors.lastName}</p>}
                                        </div>
                                    </div>

                                    <div className="mb-5.5">
                                        <label
                                            className="mb-3 block text-sm font-medium text-black dark:text-white"
                                            htmlFor="phoneNumber"
                                        >
                                            Phone Number
                                        </label>
                                        <div className="flex">
                                            <select
                                                name="countryCode"
                                                value={formData.countryCode}
                                                onChange={handleInputChange}
                                                className="w-20 rounded-l border border-stroke bg-gray py-3 px-2 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                                            >
                                                {countryCodes.map((country) => (
                                                    <option key={country.code} value={country.code}>
                                                        {country.flag} {country.code}
                                                    </option>
                                                ))}
                                            </select>
                                            <input
                                                className="w-full rounded-r border border-stroke bg-gray py-3 px-4.5 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                                                type="tel"
                                                name="phoneNumber"
                                                id="phoneNumber"
                                                value={formData.phoneNumber}
                                                onChange={handleInputChange}
                                                placeholder="Enter your phone number"
                                            />
                                        </div>
                                        {errors.phoneNumber && <p className="text-danger mt-1">{errors.phoneNumber}</p>}
                                    </div>

                                    {error && <p className="text-danger mb-4.5">{error}</p>}
                                    {success && <p className="text-success mb-4.5">{success}</p>}

                                    <div className="flex justify-end gap-4.5">
                                        <button
                                            className="flex justify-center rounded border border-stroke py-2 px-6 font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white"
                                            type="submit"
                                            onClick={() => {
                                                // Reset form to initial values
                                                if (user) {
                                                    const [countryCode, localNumber] = (user.phoneNumber || '+1').split(' ');
                                                    setFormData({
                                                        firstName: user.firstName || '',
                                                        lastName: user.lastName || '',
                                                        countryCode: countryCode || '+1',
                                                        phoneNumber: localNumber || '',
                                                    });
                                                }
                                                setErrors({firstName: '', lastName: '', phoneNumber: ''});
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="flex justify-center rounded bg-primary py-2 px-6 font-medium text-gray hover:bg-opacity-90"
                                            type="submit"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div className="col-span-5 xl:col-span-2">
                            <div
                                className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                                <div className="border-b border-stroke py-4 px-7 dark:border-strokedark">
                                    <h3 className="font-medium text-black dark:text-white">
                                        Your Photo
                                    </h3>
                                </div>
                                <div className="p-7">
                                    <div className="mb-4 flex items-center gap-3">
                                        <div className="h-14 w-14 rounded-full">
                                            <img
                                                className='rounded-full'
                                                src={photoPreview || user?.photo || userThree}
                                                alt="User Photo"
                                            />
                                        </div>
                                        <div>
                                            <span className="mb-1.5 text-black dark:text-white">
                                                Edit your photo
                                            </span>
                                            {/* <span className="flex gap-2.5">
                        <button className="text-sm hover:text-primary">
                          Delete
                        </button>
                        <button className="text-sm hover:text-primary">
                          Update
                        </button>
                      </span> */}
                                        </div>
                                    </div>

                                    <div
                                        id="FileUpload"
                                        className="relative mb-5.5 block w-full cursor-pointer appearance-none rounded border border-dashed border-primary bg-gray py-4 px-4 dark:bg-meta-4 sm:py-7.5"
                                    >
                                        <input
                                            type="file"
                                            accept="image/*"
                                            id="photo"
                                            name="photo"
                                            className="absolute inset-0 z-50 m-0 h-full w-full cursor-pointer p-0 opacity-0 outline-none"
                                            onChange={handlePhotoChange}
                                        />
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <span
                                                className="flex h-10 w-10 items-center justify-center rounded-full border border-stroke bg-white dark:border-strokedark dark:bg-boxdark">
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M1.99967 9.33337C2.36786 9.33337 2.66634 9.63185 2.66634 10V12.6667C2.66634 12.8435 2.73658 13.0131 2.8616 13.1381C2.98663 13.2631 3.1562 13.3334 3.33301 13.3334H12.6663C12.8431 13.3334 13.0127 13.2631 13.1377 13.1381C13.2628 13.0131 13.333 12.8435 13.333 12.6667V10C13.333 9.63185 13.6315 9.33337 13.9997 9.33337C14.3679 9.33337 14.6663 9.63185 14.6663 10V12.6667C14.6663 13.1971 14.4556 13.7058 14.0806 14.0809C13.7055 14.456 13.1968 14.6667 12.6663 14.6667H3.33301C2.80257 14.6667 2.29387 14.456 1.91879 14.0809C1.54372 13.7058 1.33301 13.1971 1.33301 12.6667V10C1.33301 9.63185 1.63148 9.33337 1.99967 9.33337Z"
                                                        fill="#3C50E0"
                                                    />
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M7.5286 1.52864C7.78894 1.26829 8.21106 1.26829 8.4714 1.52864L11.8047 4.86197C12.0651 5.12232 12.0651 5.54443 11.8047 5.80478C11.5444 6.06513 11.1223 6.06513 10.8619 5.80478L8 2.94285L5.13807 5.80478C4.87772 6.06513 4.45561 6.06513 4.19526 5.80478C3.93491 5.54443 3.93491 5.12232 4.19526 4.86197L7.5286 1.52864Z"
                                                        fill="#3C50E0"
                                                    />
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M7.99967 1.33337C8.36786 1.33337 8.66634 1.63185 8.66634 2.00004V10C8.66634 10.3682 8.36786 10.6667 7.99967 10.6667C7.63148 10.6667 7.33301 10.3682 7.33301 10V2.00004C7.33301 1.63185 7.63148 1.33337 7.99967 1.33337Z"
                                                        fill="#3C50E0"
                                                    />
                                                </svg>
                                            </span>
                                            <p>
                                                <span className="text-primary">Click to upload</span>
                                                or
                                                drag and drop
                                            </p>
                                            <p className="mt-1.5">SVG, PNG, JPG or GIF</p>
                                            <p>(max, 800 X 800px)</p>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-4.5">
                                        <button
                                            className="flex justify-center rounded border border-stroke py-2 px-6 font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white"
                                            type="submit"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isLoading}
                                            className="flex justify-center rounded bg-primary py-2 px-6 font-medium text-gray hover:bg-opacity-90"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </DefaultLayout>
    );
};

export default User;
